import { useMutation } from '@tanstack/react-query';
import { DEFAULT_COLORS } from '@givebutter/primitives/color-picker';
import { useToast } from '@givebutter/primitives/toast';
import { isAcceptableThemeColor } from '@givebutter/util';
import { updateAccount } from 'api/account';
import * as Form from 'components/form';
import * as SettingsCard from 'components/settings-card';
import { Settings } from 'constants/settings';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { Account } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface ThemeColorSettingsFormValues {
    themeColor: string;
}

const ThemeColorsSettings = () => {
    const toast = useToast();
    const themeColorCode = Settings.ThemeColor.Code;
    const { accountId, settings } = useCurrentAccount();
    const themeColorSetting = settings.find(item => item.code === themeColorCode);

    const methods = Form.useGivebutterForm<ThemeColorSettingsFormValues>({
        defaultValues: {
            themeColor: themeColorSetting?.value || '',
        },
    });
    const { control, handleSubmit, path } = methods;

    const { mutate: submit, isPending: isLoading } = useMutation<
        Account,
        GivebutterAxiosError,
        ThemeColorSettingsFormValues
    >({
        mutationFn: ({ themeColor }) =>
            updateAccount(accountId, {
                settings: [
                    {
                        type: 'string',
                        code: themeColorCode,
                        name: themeColorSetting?.name || 'theme_color',
                        value: themeColor,
                        active: !!themeColor,
                    },
                ],
            }),
        onSuccess: () => {
            toast.success('Updated Theme Colors!');
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.current(accountId) });
        },
        onError: (e: GivebutterAxiosError) => {
            toast.error(e.response?.data?.message ?? 'Failed to update Theme Color');
        },
    });

    return (
        <SettingsCard.Root>
            <SettingsCard.LeftSide>
                <SettingsCard.Heading>Theme Colors</SettingsCard.Heading>
            </SettingsCard.LeftSide>
            <SettingsCard.RightSide>
                <Form.Provider {...methods}>
                    <form
                        onSubmit={handleSubmit(values => submit(values))}
                        className="flex flex-col gap-4"
                    >
                        <Form.Container>
                            <Form.Label className="text-base" name={path('themeColor')}>
                                Main Theme Color
                            </Form.Label>
                            <p className="text-sm text-gray-700">
                                Used on your Landing Page and as the default on all your campaigns.
                            </p>
                            <Form.UseFormColorPicker
                                control={control}
                                name={path('themeColor')}
                                colors={DEFAULT_COLORS.filter(
                                    color => color.toUpperCase() !== '#FFFFFF',
                                )}
                                rules={{
                                    validate: value =>
                                        isAcceptableThemeColor(value)
                                            ? true
                                            : 'Choose a darker color for better readability.',
                                }}
                            />
                            <Form.Error name={path('themeColor')} />
                        </Form.Container>
                        <Form.SubmitButton isSubmitting={isLoading} className="self-start">
                            Save
                        </Form.SubmitButton>
                    </form>
                </Form.Provider>
            </SettingsCard.RightSide>
        </SettingsCard.Root>
    );
};

export { ThemeColorsSettings };
